import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { useContext, useEffect, useState } from "react";
import routes from "./Routes/Routes";
import LoaderGif from "./Assets/loader.gif";
// Scss
import "./App.css";
import "./Assets/scss/material-kit.scss?v=2.2.0";

// Components
import Sidebar from "./Components/Sidebar/Sidebar";
import BarIcon from "./Assets/images/bar.svg";
import ProtectedRoute from "./Components/ProtectedRoute/ProtectedRoute";
import PasswordWarningBanner from "./Components/PasswordWarningBanner/PasswordWarningBanner";
import { getLatest } from "./api/notification";
// Context
import { AppContext } from "./Context/AppContext";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import CustomZendeskButton from "./Components/Zendesk/zendeskBnt";
import { UserContext } from "./Context/UserContext/userContext";

const theme = createTheme({
  palette: {
    primary: { main: "#ca998d", contrastText: "#ffffff" },
  },
  components: {
    MuiTextField: {
      styleOverrides: { root: { paddingTop: 8 } },
      defaultProps: {
        variant: "standard"
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          textTransform: "capitalize",
          fontWeight: "700",
          fontSize: "20px !important",
          lineHeight: 1.5,
          "&:hover, &:focus": { backgroundColor: "#c2887b", color: "#fff" },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { fontSize: 20, fontWeight: 300, color: "#AAA" },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: 20 },
        input: {
          boxSizing: "border-box",
          height: "auto",
          padding: "6px 0 7px",
          "&.Mui-disabled": {
            WebkitTextFillColor: "#000000",
            color: "#000000",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: "#ADACAC",
          fontWeight: "700",
          fontSize: "16px",
          lineHeight: 1.5,
          "&.Mui-selected": {
            color: "#212721",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiAutocomplete-tag": {
            padding: "0px",
            color: "rgba(0, 0, 0, 0.87)",
            backgroundColor: "#e0e0e0",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          color: "rgba(0, 0, 0, 0.87)",
          backgroundColor: "#e0e0e0",
        },
      },
    },
  },
});

const App = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isPrintableOrderReceipt =
    /^\/admin\/orders\/view\/[^/]+\/receipt\/?$/.test(
      window.location.pathname
    );

  useEffect(() => {
    if (window.location.pathname !== "/admin/orders/designer-status") {
      window.scrollTo({ top: 0, left: 0 });
    }
  }, [window.location.href]);

  const { userState } = useContext(UserContext);

  const { isLogin, isLoading, alert, showToastMessage, handleGetNotificationCount } = useContext(AppContext);

  const lastNotificationId = React.useRef(null);

  useEffect(() => {
    const pollNotification = async () => {
      try {
        if (isLogin) {
          const response = await getLatest();
          if (response?.data?.data) {
            const { id, title, message } = response.data.data;

            // Only show toast if it's a new notification we haven't seen in this session
            if (lastNotificationId.current !== id) {
              lastNotificationId.current = id;
              showToastMessage(title, message, "info", false, 10000);
              handleGetNotificationCount();
            }
          }
        }
      } catch (error) {
        console.error("Error polling notifications:", error);
      }
    };

    pollNotification();
    const intervalId = setInterval(pollNotification, process.env.REACT_APP_NOTIFICATION_POLLING_INTERVAL || 60000);

    return () => clearInterval(intervalId);
  }, [isLogin]);

  //To manage the Payabli component
  //Load Payabli Component script
  useEffect(() => {
    if (isPrintableOrderReceipt) return undefined;

    const payabliComponentUrl = process.env.REACT_APP_PAYABLI_COMPOENT_URL;
    if (!payabliComponentUrl) return undefined;

    // Load Payabli Component script
    const script = document.createElement("script");
    script.src = payabliComponentUrl;
    script.async = true;
    document.body.appendChild(script);

    // Cleanup script on component unmount
    return () => {
      document.body.removeChild(script);
    };
  }, [isPrintableOrderReceipt]);

  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <div className={isLogin && !isPrintableOrderReceipt ? "wrapper" : ""}>
          {isLogin && !isPrintableOrderReceipt && (
            <>
              <button
                type="button"
                className="menuToggle d-flex d-xl-none"
                aria-label="Open menu"
                onClick={() => setSidebarOpen(true)}
              >
                <img src={BarIcon} alt="" />
              </button>
              {sidebarOpen && (
                <div
                  className="sidebarOverlay d-xl-none"
                  onClick={() => setSidebarOpen(false)}
                />
              )}
              <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
            </>
          )}
          {isLogin &&
            !isPrintableOrderReceipt &&
            userState.userProfile && (
              <CustomZendeskButton />
            )}
          <div
            className={
              !isLogin
                ? "content-login-wrapper"
                : isPrintableOrderReceipt
                  ? ""
                  : "content-wrapper rightPanel"
            }
          >
            {isLogin && !isPrintableOrderReceipt && <PasswordWarningBanner />}
            {alert}

            {isLoading && (
              <div className="loader-wrapper">
                <div
                  style={{
                    height: "100%",
                    width: "100%",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  {/* <Loader type="ThreeDots" color="#0069d9" height={50} width={50} /> */}
                  <img
                    style={{ width: "500px" }}
                    src={LoaderGif}
                    alt="Loader"
                  />
                </div>
              </div>
            )}

            <ProtectedRoute>
              <Routes>
                {routes.map((route) =>
                  route.children
                    .filter(
                      (child) =>
                        !child.superAdminOnly ||
                        userState?.userProfile?.user_type === "super_admin"
                    )
                    .map((child, idx) => {
                      return (
                        <Route
                          key={idx}
                          path={child.path}
                          Component={child.component}
                        />
                      );
                    })
                )}
              </Routes>
            </ProtectedRoute>
          </div>
        </div>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
